<script>
import { v3HandoffService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import Loader from '@/core/components/Loader/index.vue';
import { markRumHandoff } from '@ringba/telemetry/rum';
import { whitelabelManager } from '../../whitelabel';

/**
 * Redirects V2 routes to V3 by rewriting hash path to pathname (e.g. `/#/dashboard/v3` → `/dashboard/v3`).
 * The hash's query follows any query already outside the hash.
 *
 * `flag` is a route config key (`flag="'apiTokens'"`).
 * {@link v3HandoffService.handsOff} decides from it whether to redirect.
 *
 * `flag` is required.
 * A malformed value (missing, the bare-attribute string `'flag'`, or not a string) fails the prop validator, and counts as `Disabled`: fail closed rather than redirecting unconditionally.
 *
 * With `fallback` prop: when not redirecting, navigates to root instead of rendering children.
 *
 * Children must be registered synchronously.
 * ngVue moves the child's rendered element into this component's slot, but an async component first renders a comment placeholder, so it resolves outside the page.
 */
export default {
  name: 'RedirectToV3',
  components: {
    Loader,
  },
  props: {
    flag: {
      type: String,
      required: true,
      validator(value) {
        if (value === 'flag') {
          console.warn(
            'RedirectToV3: flag="flag" is a bare valueless attribute — ngVue evaluates it to the '
            + 'string \'flag\', not a route key. Use flag="\'routeKey\'" (nested quotes) to pass a route key.',
          );
          return false;
        }
        return value.length > 0;
      },
    },
    /**
     * When true, and "should redirect to V3" is false, redirect to the main dashboard instead of rendering children.
     *
     * Better naming would be `fallbackToMain` or something more verbose, but keeping `fallback` for shorthand usage:
     * ```
     * <vue-redirect-to-v3 flag="'demo'" fallback />
     * ```
     */
    fallback: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      v3Url: '',
      pathname: window.location.pathname + window.location.hash,
    };
  },
  computed: {
    shouldRedirect() {
      return v3HandoffService.handsOff(this.flag);
    },
  },
  watch: {
    shouldRedirect: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.doRedirectToV3();
        } else if (this.fallback) {
          console.warn('Redirecting to root from', window.location.hash);
          window.location.hash = '#/';
        }
      },
    },
  },
  methods: {
    doRedirectToV3() {
      // Defer to the next macrotask so that window.location (href/hash) reflects the final route after
      // the hash-based router finishes navigation. Without this, on some route transitions the
      // hash still contains the previous route, causing v3Url to be computed from stale location data.
      setTimeout(() => {
        const newUrl = new URL(window.location.href);
        const hashPath = newUrl.hash.replace('#', '');
        const queryStart = hashPath.indexOf('?');
        const hashQuery = queryStart < 0 ? '' : hashPath.slice(queryStart + 1);

        newUrl.pathname =
          (whitelabelManager.value.path || '')
          + (queryStart < 0 ? hashPath : hashPath.slice(0, queryStart));
        if (hashQuery) {
          newUrl.search += (newUrl.search ? '&' : '?') + hashQuery;
        }
        newUrl.hash = '';

        this.v3Url = newUrl.toString();
        console.warn('Redirecting to v3...', this.v3Url);
        markRumHandoff('v3');
        window.location.replace(this.v3Url);
      }, 0);
    },
  },
  render(h) {
    const slotNodes = this.$slots.default || [];
    const validChildren = slotNodes.filter(node => node.tag);

    // Redirect Mode: Render both Loader and children (children hidden with CSS)
    // This prevents ngVue error: "Cannot read properties of undefined (reading 'parentNode')"
    if (this.shouldRedirect) {
      return h('div', { class: 'redirect-page' }, [
        h(Loader),
        // Render children hidden so ngVue can mount them without errors
        h('div', {
          style: {
            display: 'none',
            position: 'absolute',
            visibility: 'hidden',
            zIndex: -1,
          },
        }, slotNodes)
      ]);
    }

    // Single Child Mode: Render directly (No Wrapper)
    if (validChildren.length === 1) {
      return validChildren[0];
    }

    // Multiple Children Mode: Render with wrapper
    return h('div', {
      style: {
        display: 'contents'
      }
    }, slotNodes);
  },
};

</script>
